import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Text } from '@modusign/design-system';
import React, { useRef } from 'react';

import InnerSearchInput from '@app/components/InnerSearchInput';
import { ResourceRoleSubject } from '@app/features/authorization/types';

interface SelectedMemberGroupAreaProps {
  selectedResourceRoleSubjects: ResourceRoleSubject[];
  keyword: string;
  onKeywordChange: (value: string) => void;
  onSelectedResourceRoleSubjectsChange: (value: ResourceRoleSubject[]) => void;
}

function SelectedMemberGroupArea({
  selectedResourceRoleSubjects,
  onSelectedResourceRoleSubjectsChange,
  keyword,
  onKeywordChange: onKeyworkdChange,
}: SelectedMemberGroupAreaProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  return (
    <Flex
      border="1px solid"
      borderColor="formBorderNeutralBase"
      borderRadius="2px"
      boxShadow="inset 1px 1px 1px rgba(0, 0, 0, 0.06)"
      flexWrap="wrap"
      maxHeight={108}
      minHeight={10}
      overflowY="auto"
      p={4}
      onClick={() => searchInputRef.current?.focus()}
    >
      {selectedResourceRoleSubjects.map((subject) => (
        <SelectedMemberGroupItem
          isGroup={subject.subjectType === 'GROUP'}
          key={subject.subjectId}
          name={subject.subjectType === 'GROUP' ? subject.groupName : subject.memberName}
          onCancel={
            !subject.automaticSharingTypes?.includes('DEFAULT')
              ? () => {
                  onSelectedResourceRoleSubjectsChange(
                    selectedResourceRoleSubjects.filter((v) => v.subjectId !== subject.subjectId)
                  );
                }
              : undefined
          }
        />
      ))}
      <InnerSearchInput
        placeholder={
          selectedResourceRoleSubjects.length === 0 ? '이름 또는 이메일로 찾기' : undefined
        }
        ref={searchInputRef}
        searchKeyword={keyword}
        onChange={onKeyworkdChange}
      />
    </Flex>
  );
}

interface SelectedMemberGroupItemProps {
  name: string;
  isGroup: boolean;
  onCancel?: () => void;
}

function SelectedMemberGroupItem({ name, isGroup, onCancel }: SelectedMemberGroupItemProps) {
  return (
    <Flex
      alignItems="center"
      bg="navigationBackgroundNeutralSlightlyDark"
      borderRadius="4px"
      color="iconNeutralBase"
      m={1}
      maxWidth="180px"
      px={3}
      py={1}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
    >
      {isGroup && (
        <Text
          as={FontAwesomeIcon}
          icon={faUserGroup as IconProp}
          mr={1}
          style={{ height: 14, width: 11 }}
        />
      )}
      <Text color="textNeutralBase" ellipsis fontSize="sm" fontWeight="button">
        {name}
      </Text>
      {onCancel && (
        <Flex
          alignItems="center"
          as="button"
          flex="1 0 auto"
          height="24px"
          justifyContent="center"
          px="5px"
          py="7.5px"
          width="24px"
          onClick={onCancel}
        >
          <Text as={FontAwesomeIcon} color="textNeutralLight" icon={faXmark as IconProp} />
        </Flex>
      )}
    </Flex>
  );
}

export default SelectedMemberGroupArea;
